import {
  HIGH_PASTE_RATIO,
  LEAVES_FLAG_THRESHOLD,
  KEYSTROKE_RATIO_MIN_CHARS,
  KEYSTROKE_RATIO_THRESHOLD,
  MULTIPLE_PASTE_FLAG_THRESHOLD,
  REVIEW_RECOMMENDED_FLAGS,
  SHORT_WRITING_MAX_ACTIVE_MINUTES,
  SHORT_WRITING_MIN_WORDS,
  SUBMISSION_PAUSE_FLAGS,
} from "./constants.js";
import {
  FLAG_MESSAGES,
  INSTRUCTOR_CONTEXT_NOTICE,
  INSTRUCTOR_NO_FLAGS,
  INSTRUCTOR_REVIEW_NOTICE,
  STEADY_MESSAGE,
  STUDENT_CONTEXT_NOTICE,
  STUDENT_NO_FLAGS,
} from "./messages.js";
import type { SubmissionReview, WritingMetrics } from "../types.js";

/**
 * The single source of truth for how writing-process evidence becomes a
 * neutral review. Runs identically in the browser (for the live student
 * summary, which updates every second) and on the server (which produces the
 * authoritative review stored with a submission).
 */
export function evaluateMetrics(metrics: WritingMetrics): SubmissionReview {
  const flags = collectFlags(metrics);

  let mode: SubmissionReview["mode"] = "inform_only";
  let badge = "Shared overview";
  let badgeClass: SubmissionReview["badgeClass"] = "audit-good";
  let reviewLevel: SubmissionReview["reviewLevel"] = "Low";
  let summaryLabel = "Writing developed mostly in the editor";
  let summary =
    "This submission shows a steady in-editor writing process with no strong follow-up signal.";

  if (flags.length >= SUBMISSION_PAUSE_FLAGS) {
    mode = "submission_pause";
    badge = "Instructor follow-up";
    badgeClass = "audit-pause";
    reviewLevel = "High";
    summaryLabel = "Limited in-editor writing evidence";
    summary =
      "This submission includes several review conditions that may need instructor follow-up with course context.";
  } else if (flags.length >= REVIEW_RECOMMENDED_FLAGS) {
    mode = "review_recommended";
    badge = "Support review";
    badgeClass = "audit-warn";
    reviewLevel = "Moderate";
    summaryLabel = "Mixed writing process overview";
    summary =
      "This submission shows mixed process signals and may benefit from a context-aware instructor review.";
  }

  return {
    mode,
    badge,
    badgeClass,
    reviewLevel,
    summaryLabel,
    summary,
    flags,
    steadyMessage: STEADY_MESSAGE,
    instructorReviewNotice: INSTRUCTOR_REVIEW_NOTICE,
    instructorMessages: buildInstructorMessages(flags),
    studentMessages: buildStudentMessages(flags),
  };
}

/** Applies each flag rule in the fixed order the review panel expects. */
export function collectFlags(metrics: WritingMetrics): string[] {
  const charCount = metrics.totalCharacters;
  const wordCount = metrics.wordCount;
  // The high-paste rule compares pasted WORDS with the draft's WORD count, so
  // the ratio is something a grader can quantify directly. `pastedCharacters`
  // is still recorded in the metrics but no longer drives this rule.
  const pasteRatio = wordCount > 0 ? metrics.pastedWords / wordCount : 0;
  const keystrokeRatio = charCount > 0 ? metrics.keystrokeCount / charCount : 0;
  const activeMinutes = metrics.activeWritingMs / 60000;
  const flags: string[] = [];

  if (metrics.pasteEvents >= 1) {
    flags.push(FLAG_MESSAGES.pasteDetected);
  }
  if (
    wordCount >= SHORT_WRITING_MIN_WORDS &&
    activeMinutes > 0 &&
    activeMinutes < SHORT_WRITING_MAX_ACTIVE_MINUTES
  ) {
    flags.push(FLAG_MESSAGES.shortWritingTime);
  }
  if (charCount > KEYSTROKE_RATIO_MIN_CHARS && keystrokeRatio < KEYSTROKE_RATIO_THRESHOLD) {
    flags.push(FLAG_MESSAGES.keystrokeMismatch);
  }
  if (metrics.pasteEvents >= MULTIPLE_PASTE_FLAG_THRESHOLD) {
    flags.push(FLAG_MESSAGES.multiplePastes);
  }
  if (pasteRatio >= HIGH_PASTE_RATIO) {
    flags.push(FLAG_MESSAGES.highPasteRatio);
  }
  if (metrics.blurEvents >= LEAVES_FLAG_THRESHOLD) {
    flags.push(FLAG_MESSAGES.focusChanges);
  }

  return flags;
}

export function buildInstructorMessages(flags: string[]): string[] {
  if (flags.length === 0) {
    return [INSTRUCTOR_NO_FLAGS];
  }
  return [...flags, INSTRUCTOR_CONTEXT_NOTICE];
}

export function buildStudentMessages(flags: string[]): string[] {
  if (flags.length === 0) {
    return [STUDENT_NO_FLAGS];
  }
  return [...flags, STUDENT_CONTEXT_NOTICE];
}

/**
 * Whether anything has actually been recorded yet. The student panel stays
 * blank until this is true so reminders never appear before writing starts.
 */
export function hasWritingActivity(metrics: WritingMetrics): boolean {
  return (
    metrics.keystrokeCount > 0 ||
    metrics.pasteEvents > 0 ||
    metrics.totalCharacters > 0 ||
    metrics.activeWritingMs > 0
  );
}
